import React, { useContext, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { RootStateOrAny, useSelector } from "react-redux";
import { performancePages } from "../../../../constants";
import { ThemeContext } from "../../../../context";
import BrandPerformanceGraph from "../BrandPerformanceGraph";

import { withRouter, RouteComponentProps } from "react-router-dom";

import { getBrandImageAttributesAverage, getBrandImageAttributesTrend } from "../../../../api/brandPerformanceApi";
import BrandPerformanceTrendTile from "../BrandPerformanceTrendTile";
import BrandPerformanceTableTile from "../BrandPerformanceTableTile";
import { isNull, isNullOrUndefined } from "../../../../utils/utilityFunctions";

interface Props extends RouteComponentProps {
  onHandleRadioClick: any;
  nissanCompetitor: string;
  activePage: performancePages;
  diff: number;
  granularity: string;
}

const TableTooltipParagraphs = () => (
  <>
    <p>
      <span style={{ fontWeight: "bold" }}>Brand Image </span>
      considers the ratings given by public opinion to car models and brands based on their attributes. Please note that because the data
      collection methos is different between each country, the figure can not be compared as absolute value.
    </p>
    <p>
      <u>Source</u>: GfK Imagery and Awareness
    </p>
    <br />
  </>
);

const TrendTooltipParagraph = () => (
  <>
    <p>
      <span style={{ fontWeight: "bold" }}>Brand Image average </span>
      considers the average score given to a make/model in the selected period. Please note that Image average is calculated by the sum of
      all scores divided by the number of months selected.
    </p>
    <p>
      <u>Source</u>: GfK Imagery and Awareness
    </p>
    <br />
  </>
);

const BrandImageAttributes = withRouter((props: Props) => {
  const { theme } = useContext(ThemeContext);
  const { onHandleRadioClick, nissanCompetitor, activePage, diff, history, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);

  // fetch the trend and average data
  const {
    data: chartData,
    isFetching: loadingTrend, // call data using react-query
    refetch: refetchVBrandImageAttributesTrend,
  } = useQuery(
    ["brandImageAttributesTrend", granularity, history.location.search],
    () => getBrandImageAttributesTrend({ ...params, nissan_competitor: nissanCompetitor }),
    {
      enabled: false,
    }
  );

  const {
    data: tableData,
    isFetching: loadingTable,
    refetch: refetchBrandImageAttributesAverage,
  } = useQuery(
    ["brandImageAttributesAverage", granularity, history.location.search],
    () => getBrandImageAttributesAverage({ ...params, nissan_competitor: nissanCompetitor }),
    {
      enabled: false,
    }
  );

  // when the url changes, refetch the data
  useEffect(() => {
    refetchBrandImageAttributesAverage();
    refetchVBrandImageAttributesTrend();
  }, [history.location.search]);

  const { market, granularityParams, model } = params;

  const columns = [
    {
      id: "model",
      Header: `${model == "Brand" ? "Brand" : "Model"} `,
      accessor: "model",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "alphanumericIgnoreCase",
      Cell: (props1: { value: string }) => {
        const { value } = props1;

        return value;
      },
    },
    {
      Header: "Image Attribute Average",
      accessor: `percentage_average_score`,
      id: "percentage_average_score",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "alphanumericCustom",
      Cell: (props: { cell: { row: { original: any } } }) => {
        const {
          cell: {
            row: { original },
          },
        } = props;
        const value = original?.["percentage_average_score"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
    {
      Header: "YoY",
      accessor: `yoy`,
      id: "yoy",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props: { cell: { row: { original: any } } }) => {
        const {
          cell: {
            row: { original },
          },
        } = props;
        const value = original?.["yoy"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
  ];

  const dateRange = tableData?.date_range ?? [];

  const dateTitle = useMemo(() => {
    if (dateRange && dateRange.length > 1) {
      return `(${dateRange[0]} - ${dateRange.slice(-1)})`;
    }

    return "";
  }, [dateRange]);

  // Pad chartData.data if only 2 points (for quarterly charts)
  const paddedChartData = useMemo(() => {
    if (!chartData || !Array.isArray(chartData.data) || chartData.data.length !== 2) return chartData;
    // Find all series fields
    const seriesList: Array<{ field: string }> = chartData.series || [];
    // Get the two dates and sort
    const dates: string[] = chartData.data.map((d: any) => d.date).sort();
    const moment = require("moment");
    const minDate = moment(dates[0]);
    const maxDate = moment(dates[1]);
    // Generate all quarters between min and max (should be 3: start, middle, end)
    const allQuarters: string[] = [];
    let current = minDate.clone().startOf("quarter");
    while (current.isSameOrBefore(maxDate)) {
      allQuarters.push(current.format("YYYY-MM-DD"));
      current = current.clone().add(1, "quarter");
    }
    // Build padded data
    const paddedData = allQuarters.map((date) => {
      const found = chartData.data.find((d: any) => d.date === date);
      const row: Record<string, any> = { date };
      seriesList.forEach(({ field }: { field: string }) => {
        row[field] = found && found[field] != null ? Number(found[field]) : null;
      });
      return row;
    });
    return { ...chartData, data: paddedData };
  }, [chartData]);

  // china needs a different paddedChartData logic it should check if the data has 3 points and then pad it to 4 points
  const isChinaMarket = market === "China";
  const paddedChartDataChina = useMemo(() => {
    if (!isChinaMarket || !chartData || !Array.isArray(chartData.data) || chartData.data.length !== 3) return paddedChartData;
    // Find all series fields
    const seriesList: Array<{ field: string }> = chartData.series || [];
    // Get the three dates and sort
    const dates: string[] = chartData.data.map((d: any) => d.date).sort();
    const moment = require("moment");
    const minDate = moment(dates[0]);
    const maxDate = moment(dates[2]);
    // Generate all quarters between min and max (should be 4: start, middle1, middle2, end)

    const allQuarters: string[] = [];
    let current = minDate.clone().startOf("quarter");
    while (current.isSameOrBefore(maxDate)) {
      allQuarters.push(current.format("YYYY-MM-DD"));
      current = current.clone().add(1, "quarter");
    }
    // Build padded data
    const paddedData = allQuarters.map((date) => {
      const found = chartData.data.find((d: any) => d.date === date);
      const row: Record<string, any> = { date };
      seriesList.forEach(({ field }: { field: string }) => {
        row[field] = found && found[field] != null ? Number(found[field]) : null;
      });
      return row;
    });
    return { ...chartData, data: paddedData };
  }, [chartData, isChinaMarket]);

  return (
    <div className="brand-market-main-content">
      <BrandPerformanceTrendTile
        title="Brand Image Attribute Trend"
        chartId="image_attr_chart"
        chartData={isChinaMarket ? paddedChartDataChina : paddedChartData}
        TrendTooltipParagraph={TrendTooltipParagraph}
        loadingTrend={loadingTrend}
        diff={diff}
        nissanCompetitor={nissanCompetitor}
        isPercentage={true}
        onHandleRadioClick={onHandleRadioClick}
        chartGranularity={granularity}
      />
      <BrandPerformanceTableTile
        title="Brand Image Attribute Average"
        tableId="image_attr_table"
        tableData={tableData}
        columns={columns}
        TableTooltipParagraphs={TableTooltipParagraphs}
        loadingTable={loadingTable}
        initialSortCol={{ id: "percentage_average_score", desc: true }}
      />
    </div>
  );
});

export default BrandImageAttributes;
